import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Lock, ArrowRight, ChevronDown, Check, Globe, Wifi, QrCode } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
type LanguageOption = {
  code: string;
  name: string;
  nativeName: string;
  disabled?: boolean;
};
type DemoRole = 'guest' | 'fornitore' | 'host';
const languages: LanguageOption[] = [{
  code: 'it',
  name: 'Italian',
  nativeName: 'Italiano'
}, {
  code: 'en',
  name: 'English',
  nativeName: 'English'
}, {
  code: 'fr',
  name: 'French',
  nativeName: 'Français',
  disabled: true
}, {
  code: 'es',
  name: 'Spanish',
  nativeName: 'Español',
  disabled: true
}, {
  code: 'de',
  name: 'German',
  nativeName: 'Deutsch',
  disabled: true
}, {
  code: 'zh',
  name: 'Chinese',
  nativeName: '中文',
  disabled: true
}];
const roleConfig = {
  guest: {
    emoji: '👋',
    label: 'Guest'
  },
  fornitore: {
    emoji: '🧹',
    label: 'Fornitore'
  },
  host: {
    emoji: '🏠',
    label: 'Host'
  }
};
const qrCodes = {
  fornitore: 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=fornitore-login-villa-serenity',
  host: 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=host-login-villa-serenity'
};
export function LoginPage() {
  const navigate = useNavigate();
  const {
    login
  } = useAuth();
  const {
    t,
    language,
    setLanguage
  } = useLanguage();
  const [selectedRole, setSelectedRole] = useState<DemoRole>('guest');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const roleDropdownRef = useRef<HTMLDivElement>(null);
  const currentLanguage = languages.find(l => l.code === language) || languages[0];
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target as Node)) {
        setIsRoleDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleLanguageSelect = (lang: LanguageOption) => {
    if (lang.disabled) return;
    setLanguage(lang.code as 'it' | 'en');
    setIsLanguageDropdownOpen(false);
  };
  const handleRoleSelect = (role: DemoRole) => {
    setSelectedRole(role);
    setIsRoleDropdownOpen(false);
  };
  const handleGuestLogin = () => {
    login('guest');
    navigate('/guest');
  };
  const handleStaffLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedRole !== 'guest') {
      login(selectedRole as UserRole);
      navigate(`/${selectedRole}`);
    }
  };
  const roles: DemoRole[] = ['guest', 'fornitore', 'host'];
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-1277">
      {/* Topbar with Role Selector Dropdown */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm" data-id="element-1278">
        <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-1279">
          <div className="h-16 flex items-center justify-between" data-id="element-1280">
            {/* Logo */}
            <img src="https://cdn.magicpatterns.com/uploads/7iQoZBVrzgoaKtM7t68NLA/CLEANAI_LOGO_V2.png" alt="CleanAI Logo" className="h-10 w-auto object-contain" data-id="element-1281" />

            {/* Role Selector Dropdown - Centered */}
            <div className="absolute left-1/2 -translate-x-1/2" data-id="element-1282">
              <div className="relative" ref={roleDropdownRef} data-id="element-1283">
                <button onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)} className="flex items-center gap-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-full px-4 py-2 transition-all duration-200 shadow-sm hover:shadow group h-10" data-id="element-1284">
                  <span className="text-base" data-id="element-1285">
                    {roleConfig[selectedRole].emoji}
                  </span>
                  <span className="text-sm font-semibold text-navy" data-id="element-1286">
                    {roleConfig[selectedRole].label}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isRoleDropdownOpen ? 'rotate-180' : ''}`} data-id="element-1287" />
                </button>

                <AnimatePresence data-id="element-1288">
                  {isRoleDropdownOpen && <motion.div initial={{
                  opacity: 0,
                  y: 8,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 8,
                  scale: 0.95
                }} transition={{
                  duration: 0.15,
                  ease: 'easeOut'
                }} className="absolute left-1/2 -translate-x-1/2 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-2 overflow-hidden z-50" data-id="element-1289">
                      <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-1290">
                        Seleziona Ruolo
                      </div>
                      {roles.map(role => <button key={role} onClick={() => handleRoleSelect(role)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-gray-50 ${selectedRole === role ? 'bg-gray-50 font-medium' : ''}`} data-id="element-1291">
                          <span className="text-base" data-id="element-1292">
                            {roleConfig[role].emoji}
                          </span>
                          <span className="text-navy" data-id="element-1293">
                            {roleConfig[role].label}
                          </span>
                          {selectedRole === role && <Check className="w-4 h-4 text-teal-deep ml-auto" data-id="element-1294" />}
                        </button>)}
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>

            {/* Language Selector */}
            <div className="relative" ref={languageDropdownRef} data-id="element-1295">
              <button onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)} className="flex items-center gap-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-full px-4 py-2 transition-all duration-200 shadow-sm hover:shadow group h-10" data-id="element-1296">
                <Globe className="w-4 h-4 text-gray-500 group-hover:text-navy transition-colors" data-id="element-1297" />
                <span className="text-sm font-semibold text-navy" data-id="element-1298">
                  {currentLanguage.nativeName}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isLanguageDropdownOpen ? 'rotate-180' : ''}`} data-id="element-1299" />
              </button>

              <AnimatePresence data-id="element-1300">
                {isLanguageDropdownOpen && <motion.div initial={{
                opacity: 0,
                y: 8,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: 8,
                scale: 0.95
              }} transition={{
                duration: 0.15,
                ease: 'easeOut'
              }} className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 overflow-hidden z-50" data-id="element-1301">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-1302">
                      Select Language
                    </div>
                    {languages.map(lang => <button key={lang.code} onClick={() => handleLanguageSelect(lang)} disabled={lang.disabled} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${lang.disabled ? 'text-gray-300 cursor-not-allowed bg-gray-50/50' : 'text-navy hover:bg-gray-50 cursor-pointer'} ${language === lang.code ? 'bg-gray-50 font-medium' : ''}`} data-id="element-1303">
                        <span className={lang.disabled ? 'opacity-50' : ''} data-id="element-1304">
                          {lang.nativeName}
                        </span>
                        {language === lang.code && <Check className="w-4 h-4 text-teal-deep" data-id="element-1305" />}
                        {lang.disabled && <span className="text-[10px] bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded border border-gray-200" data-id="element-1306">
                            Soon
                          </span>}
                      </button>)}
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6" data-id="element-1307">
        <AnimatePresence mode="wait" data-id="element-1308">
          <motion.div key={selectedRole} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.25
        }} className="w-full max-w-md" data-id="element-1309">
            {selectedRole === 'guest' /* Guest Login - Simple CTA + WiFi Info */ ? <div className="space-y-6" data-id="element-1310">
                <Card className="p-8 border border-gray-200 shadow-lg bg-white" data-id="element-1311">
                  <div className="text-center" data-id="element-1312">
                    <div className="text-7xl mb-6" data-id="element-1313">👋</div>
                    <h1 className="text-2xl font-bold text-navy mb-2" data-id="element-1314">
                      Benvenuto a Villa Serenity
                    </h1>
                    <p className="text-gray-500 mb-8" data-id="element-1315">
                      Accedi subito ai servizi della villa senza registrazione
                    </p>
                    <Button onClick={handleGuestLogin} className="w-full h-14 text-lg font-semibold bg-teal-deep hover:bg-teal-700 text-white shadow-lg shadow-teal-deep/20" data-id="element-1316">
                      Accedi come Ospite
                      <ArrowRight className="w-5 h-5" data-id="element-1317" />
                    </Button>
                  </div>
                </Card>

                {/* WiFi Information Card */}
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} data-id="element-1318">
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5" data-id="element-1319">
                    <div className="flex items-center justify-between gap-4" data-id="element-1320">
                      <div className="flex-1 min-w-0" data-id="element-1321">
                        <div className="flex items-center gap-2.5 mb-3" data-id="element-1322">
                          <div className="p-1.5 bg-blue-50 rounded-lg" data-id="element-1323">
                            <Wifi className="w-4 h-4 text-blue-500" data-id="element-1324" />
                          </div>
                          <h3 className="text-base font-bold text-navy" data-id="element-1325">
                            {t.common.wifi.title}
                          </h3>
                        </div>
                        <div className="space-y-0.5" data-id="element-1326">
                          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-1327">
                            {t.common.wifi.password}
                          </p>
                          <p className="text-lg font-mono font-bold text-navy tracking-tight" data-id="element-1328">
                            VillaSerenity2024
                          </p>
                        </div>
                      </div>
                      <div className="flex-shrink-0 bg-white p-1.5 rounded-lg border border-gray-100" data-id="element-1329">
                        <QrCode className="w-16 h-16 text-navy" strokeWidth={1.5} data-id="element-1330" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div> /* Staff Login - QR or Credentials */ : <Card className="p-8 border border-gray-200 shadow-lg bg-white" data-id="element-1331">
                <div className="text-center mb-6" data-id="element-1332">
                  <div className="text-5xl mb-3" data-id="element-1333">
                    {roleConfig[selectedRole].emoji}
                  </div>
                  <h1 className="text-2xl font-bold text-navy" data-id="element-1334">
                    Accesso {roleConfig[selectedRole].label}
                  </h1>
                </div>

                {/* QR Code */}
                <div className="flex justify-center mb-6" data-id="element-1335">
                  <div className="p-4 bg-white border-2 border-gray-200 rounded-xl" data-id="element-1336">
                    <img src={qrCodes[selectedRole]} alt={`QR Code ${selectedRole}`} className="w-36 h-36" data-id="element-1337" />
                  </div>
                </div>

                {/* Divider */}
                <div className="relative py-4" data-id="element-1338">
                  <div className="absolute inset-0 flex items-center" data-id="element-1339">
                    <div className="w-full border-t border-gray-200" data-id="element-1340"></div>
                  </div>
                  <div className="relative flex justify-center text-sm" data-id="element-1341">
                    <span className="px-4 bg-white text-gray-400 font-medium" data-id="element-1342">
                      oppure
                    </span>
                  </div>
                </div>

                {/* Login Form */}
                <form onSubmit={handleStaffLogin} className="space-y-4" data-id="element-1343">
                  <div data-id="element-1344">
                    <div className="relative" data-id="element-1345">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1346" />
                      <input type="text" value={username} onChange={e => setUsername(e.target.value)} className="w-full h-12 pl-10 pr-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-navy focus:border-transparent outline-none transition-all text-sm" placeholder="Username" data-id="element-1347" />
                    </div>
                  </div>

                  <div data-id="element-1348">
                    <div className="relative" data-id="element-1349">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1350" />
                      <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full h-12 pl-10 pr-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-navy focus:border-transparent outline-none transition-all text-sm" placeholder="Password" data-id="element-1351" />
                    </div>
                  </div>

                  <Button type="submit" variant="secondary" className="w-full h-12 font-medium" data-id="element-1352">
                    {t.login.loginButton}
                  </Button>
                </form>
              </Card>}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}